// @flow

import React from 'react';
import { connect } from 'react-redux';
import styled, {
  css,
  ThemeProvider,
  withTheme,
} from 'styled-components';
import Button from '../button';
import DeliveryTime from '../deliveryTime';
import { Checkbox, Label } from '../form';
import { Box, Flex } from '../grid';
import { breakpointsToArray, media } from '../../helpers';
import type { Theme } from '../../types';
import { compareEnabled } from '../../store/compareSettings';
import {
  showDeliveryTimeText,
  getIsNotOrderable,
} from '../../store/pdb/selectors';
import { getPdbCTAFormData } from '../../store/componentSettings';
import { ACFFormidable } from '../formidable';

type OwnProps = {|
  addToComparisonText: string,
  buttonLink: string,
  buttonText: string,
  canCompare: boolean,
  deliveryTimeText: string,
  inComparison: boolean,
  onCompare: Function,
  removeFromComparisonText: string,
  theme: any,
|};

type StateProps = {|
  isCompareEnabled: boolean,
  showDeliveryTime: boolean,
  pdbCTAFormData?: any,
  isNotOrderable?: boolean,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
|};

const ButtonWrapper = styled<typeof Box, Theme>(Box)`
  margin-top: 1.5rem;

  ${media.lg(css`
    margin: 0.3rem 1.5rem 0.3rem 0;
  `)}
`;

const CheckboxLabel = styled<any, Theme>('span')`
  color: ${props => props.theme.pdbCTA.checkboxLabelColor};
  font-size: 0.875rem;
`;

const StyledCheckbox = styled<typeof Checkbox, Theme>(Checkbox)`
  margin: 0 0.5em;
`;

const FlexWrapper = styled<typeof Flex, Theme>(Flex)`
  flex-direction: column;

  ${media.lg(css`
    flex-direction: row;
  `)}
`;

const PdbCTA = ({
  addToComparisonText,
  buttonLink,
  buttonText,
  canCompare,
  deliveryTimeText,
  inComparison,
  onCompare,
  removeFromComparisonText,
  theme,
  isCompareEnabled,
  showDeliveryTime,
  pdbCTAFormData,
  isNotOrderable,
}: Props) => {
  const deliveryTheme = {
    ...theme,
    deliveryTime: {
      ...theme.deliveryTime,
      color: theme.pdbCTA.deliverytimeColor,
    },
  };

  return (
    <>
      {!isNotOrderable && (
        <FlexWrapper>
          <Flex
            flexDirection="column"
            order={breakpointsToArray({
              lg: 1,
              default: 0,
            })}
          >
            <Box>
              {showDeliveryTime && (
                <ThemeProvider theme={deliveryTheme}>
                  <DeliveryTime>{deliveryTimeText}</DeliveryTime>
                </ThemeProvider>
              )}
            </Box>
            {isCompareEnabled && (
              <Flex>
                <Label>
                  <StyledCheckbox
                    checked={inComparison}
                    disabled={!canCompare}
                    error={false}
                    name="compare"
                    className=""
                    onChange={() => onCompare()}
                  />
                  <CheckboxLabel>
                    {inComparison ? removeFromComparisonText : addToComparisonText}
                  </CheckboxLabel>
                </Label>
              </Flex>
            )}
          </Flex>
          <ButtonWrapper data-cy="buttonCallToActionWrapper">
            <Button
              caret
              fullWidth
              href={buttonLink}
            >
              {buttonText}
            </Button>
          </ButtonWrapper>
        </FlexWrapper>
      )}
      {isNotOrderable && pdbCTAFormData && (
        <ACFFormidable form_data={pdbCTAFormData} />
      )}
    </>
  );
};

PdbCTA.defaultProps = {
  pdbCTAFormData: null,
  isNotOrderable: false,
};

const mapStateToProps = (state: *): StateProps => ({
  isCompareEnabled: compareEnabled(state),
  showDeliveryTime: showDeliveryTimeText(state),
  pdbCTAFormData: getPdbCTAFormData(state),
  isNotOrderable: getIsNotOrderable(state),
});

export default connect<any, OwnProps, StateProps, any, any, any>(
  mapStateToProps,
)(withTheme(PdbCTA));
